/**
 * 状态语义映射（UI-SPEC-001 / UI-TOKENS.md §10）
 *
 * 目标：把各业务域五花八门的后端状态值（state / statusCd / stateName ...）
 * 统一映射为语义类型 + 展示文案，供 DsStatusTag 使用。
 *
 * 铁律（WCAG 1.4.1）：状态**必须**同时有文字，���色仅作辅助，
 * 因此本模块一律返回 label，绝不只返回颜色。
 *
 * 原则：
 *  1. 不改后端状态值，只在前端做展示层映射；
 *  2. 未命中映射时返回 muted 类型 + 原始文案（安全降级，不丢信息）；
 *  3. 业务域（domain）可选，未指定时按「通用语义词」匹配。
 */

/** 语义类型白名单 */
export const DS_STATUS_TYPES = ['success', 'warning', 'danger', 'info', 'primary', 'muted']

/**
 * 通用语义词 -> 类型
 * 键统一小写，匹配时对状态文案做小写化后判断包含关系。
 */
const SEMANTIC_KEYWORDS = {
  success: [
    '完成', '成功', '通过', '已审', '正常', '在线', '启用', '生效',
    '已缴', '已收', '已结', '已交', '已验收', '已入库', '已发放', '已核销',
    '已支付', '已退款', '已生效', '合格', '完好', 'done', 'finish', 'success',
    'pass', 'audit', 'valid', 'online', 'enabled', 'paid', 'closed'
  ],
  warning: [
    '待', '进行中', '处理中', '审核中', '审批中', '待审', '即将', '临期', '超时',
    '待确认', '待派单', '待回访', '草稿', '未开始', '部分',
    'wait', 'pending', 'process', 'doing', 'draft', 'partial', 'soon'
  ],
  danger: [
    '驳回', '拒绝', '失败', '异常', '违规', '逾期', '已过期', '作废', '欠费',
    '不通过', '失效', '未缴', '退租', '超时',
    'reject', 'fail', 'error', 'overdue', 'expired', 'invalid', 'arrear'
  ],
  info: [
    '新建', '未提交', '未开始', '初始', '草稿',
    'new', 'init', 'draft', 'unsubmit'
  ],
  primary: [
    '在办', '办理中', '进行中', '已提交', '已接单', '已派单', '已预约', '待确认',
    'processing', 'submitted', 'accepted', 'booked'
  ]
}

/**
 * 按业务域定义的状态字典。
 * key 建议使用后端实际返回的状态值（字符串或数字）。
 * value: { type, label }，label 缺省时回落到状态原文。
 */
const DOMAIN_MAPS = {
  /* 报修工单 */
  repair: {
    '0': { type: 'warning', label: '待派单' },
    '1': { type: 'primary', label: '处理中' },
    '2': { type: 'success', label: '已完成' },
    '3': { type: 'warning', label: '待回访' },
    '4': { type: 'danger', label: '已逾期' },
    W: { type: 'primary', label: '处理中' },
    C: { type: 'success', label: '已完成' }
  },
  /* 巡检任务 */
  inspection: {
    '0': { type: 'warning', label: '待执行' },
    '1': { type: 'primary', label: '执行中' },
    '2': { type: 'success', label: '已完成' },
    '3': { type: 'danger', label: '已逾期' }
  },
  /* 费用 / 缴费 */
  fee: {
    '0': { type: 'danger', label: '未缴费' },
    '1': { type: 'success', label: '已缴费' },
    '2': { type: 'warning', label: '部分缴费' },
    '3': { type: 'muted', label: '已作废' }
  },
  /* 优惠 / 券 */
  coupon: {
    '0': { type: 'warning', label: '未使用' },
    '1': { type: 'success', label: '已使用' },
    '2': { type: 'muted', label: '已过期' },
    '3': { type: 'info', label: '已发放' }
  },
  /* 房屋优惠申请（apply_room_discount.state）：
   * 1 申请验房 / 2 验房通过 / 3 验房不通过 / 4 审批通过 / 5 审批不通过 */
  applyRoomDiscount: {
    '1': { type: 'primary', label: '申请验房' },
    '2': { type: 'success', label: '验房通过' },
    '3': { type: 'danger', label: '验房不通过' },
    '4': { type: 'success', label: '审批通过' },
    '5': { type: 'danger', label: '审批不通过' }
  },
  /* 房屋优惠申请使用状态（inUse）：0 未使用 / 1 已使用 */
  applyRoomDiscountInUse: {
    '0': { type: 'warning', label: '未使用' },
    '1': { type: 'success', label: '已使用' }
  },
  /* 预约 / 订单
   * 注意：reserve_goods_order.state 的真实取值为 S/F/W/WP/CL（见 t_dict
   * 'reserve_goods_order'.'state'），与下方的 '0'~'4' 并存，两套都保留，
   * 避免任何依赖旧值的页面失效。 */
  reserve: {
    '0': { type: 'warning', label: '待确认' },
    '1': { type: 'primary', label: '已确认' },
    '2': { type: 'primary', label: '进行中' },
    '3': { type: 'success', label: '已完成' },
    '4': { type: 'muted', label: '已取消' },
    S: { type: 'success', label: '预约成功' },
    F: { type: 'danger', label: '预约失败' },
    W: { type: 'warning', label: '待审核' },
    WP: { type: 'warning', label: '待支付' },
    CL: { type: 'muted', label: '取消预约' }
  },
  /* 预约时段（reserve_goods_order_time.state）：W 待核销 / C 核销完成 / CL 取消 */
  reserveTime: {
    W: { type: 'warning', label: '待核销' },
    C: { type: 'success', label: '已核销' },
    CL: { type: 'muted', label: '已取消' }
  },
  /* 场地（community_space.state）：1001 可预约 / 2002 不可预约 */
  communitySpace: {
    '1001': { type: 'success', label: '可预约' },
    '2002': { type: 'muted', label: '不可预约' }
  },
  /* 场地预约订单（community_space_person.state）：
   * S 预约成功 / F 预约失败 / W 待审核 / WP 待支付 / CL 取消预约
   * 来源：t_dict (table_name='community_space_person', table_columns='state')
   * 与 CommunitySpacePersonDto 的 STATE_S / STATE_F / STATE_W / STATE_WP / STATE_CL 一致。
   * 注意：t_dict 中「待支付」历史上误写为 'F'（与「预约失败」重复），实际取值以 Dto 常量 WP 为准。 */
  communitySpacePerson: {
    S: { type: 'success', label: '预约成功' },
    F: { type: 'danger', label: '预约失败' },
    W: { type: 'warning', label: '待审核' },
    WP: { type: 'warning', label: '待支付' },
    CL: { type: 'muted', label: '取消预约' }
  },
  /* 巡检任务 / 保养任务（inspection_task.state、maintainance_task.state）：
   * 20200405 未开始 / 20200406 巡检中 / 20200407 巡检完成 / 20200408 已超时 / 20200409 缺勤 */
  inspectionTask: {
    '20200405': { type: 'warning', label: '未开始' },
    '20200406': { type: 'primary', label: '巡检中' },
    '20200407': { type: 'success', label: '巡检完成' },
    '20200408': { type: 'danger', label: '已超时' },
    '20200409': { type: 'danger', label: '缺勤' }
  },
  /* 保养任务（maintainance_task.state）：与巡检任务同码不同义！
   * 20200405 未开始 / 20200406 待验收 / 20200407 巡检完成 / 20200408 已超时 / 20200409 缺勤
   * 注意 20200406 在 inspection_task 中是「巡检中」(primary)，
   * 在 maintenance_task 中是「待验收」(warning)，必须分域映射，不能共用。 */
  maintenanceTask: {
    '20200405': { type: 'warning', label: '未开始' },
    '20200406': { type: 'warning', label: '待验收' },
    '20200407': { type: 'success', label: '巡检完成' },
    '20200408': { type: 'danger', label: '已超时' },
    '20200409': { type: 'danger', label: '缺勤' }
  },
  /* 巡检计划（inspection_plan.state）：
   * 2020025 启用 / 2020026 停用 */
  inspectionPlan: {
    '2020025': { type: 'success', label: '启用' },
    '2020026': { type: 'muted', label: '停用' }
  },
  /* 保养计划（maintainance_plan.state）：
   * 2020025 启用 / 2020026 停用 */
  maintenancePlan: {
    '2020025': { type: 'success', label: '启用' },
    '2020026': { type: 'muted', label: '停用' }
  },
  /* 产权登记（property_right_registration.state）：
   * 0 未审核 / 1 审核通过 / 2 审核不通过
   * 注意：页面中「审核」按钮以 state !== '1' 为判定条件。 */
  propertyRightRegistration: {
    '0': { type: 'warning', label: '未审核' },
    '1': { type: 'success', label: '审核通过' },
    '2': { type: 'danger', label: '审核不通过' }
  },
  /* 小区（s_community.state，字典表 t_dict: table_name='s_community', table_columns='state'）：
   * 1000 待审核 / 1100 审核通过 / 1200 拒绝审核
   * 数据来源：docs/db/TT.sql 的 t_dict 初始化数据；
   * 后端 CommunityDto.STATE_NORMAL = "1100" 亦印证 1100 为正常态。
   * 注意：此处不可与「我的小区」(s_community_member.audit_status_cd) 混用，
   * 后者是 1000 待审核 / 1100 审核完成 / 1200 审核拒绝，属于不同业务域。 */
  community: {
    '1000': { type: 'warning', label: '待审核' },
    '1100': { type: 'success', label: '审核通过' },
    '1200': { type: 'danger', label: '拒绝审核' }
  },
  /* 房屋装修（room_renovation.state，字典表 t_dict: table_name='room_renovation', table_columns='state'）：
   * 1000 待审核 / 2000 审核不通过 / 3000 装修中 / 4000 待验收 / 5000 验收成功 / 6000 验收失败
   * 注意：页面操作按钮的显示条件依赖这些状态码（如 1000 出现「审核」、3000 出现「装修完成」、
   * 4000 出现「装修验收」、5000/6000 出现「验收明细」），不可改动码值。 */
  roomRenovation: {
    '1000': { type: 'warning', label: '待审核' },
    '2000': { type: 'danger', label: '审核不通过' },
    '3000': { type: 'primary', label: '装修中' },
    '4000': { type: 'warning', label: '待验收' },
    '5000': { type: 'success', label: '验收成功' },
    '6000': { type: 'danger', label: '验收失败' }
  },
  /* 我的小区（s_community_member.audit_status_cd）：
   * 1000 待审核 / 1100 审核完成 / 1200 审核拒绝
   * 来源：CommunityServiceDaoImplMapper.xml 的 CASE WHEN 表达式。
   * 注意：与「小区」(s_community.state) 是不同业务域，码值巧合相同但语义不同。 */
  enterCommunity: {
    '1000': { type: 'warning', label: '待审核' },
    '1100': { type: 'success', label: '审核完成' },
    '1200': { type: 'danger', label: '审核拒绝' }
  },
  /* 装修记录（room_renovation_record.state，字典表 t_dict: table_name='room_renovation_record', table_columns='state'）：
   * 1000 待审核 / 2000 审核不通过 / 3000 装修中 / 4000 待验收 / 5000 验收成功 / 6000 验收失败
   * 注意：与「房屋装修」(room_renovation.state) 码值一致但语义相同，属于同一业务链，
   * 单独建域以免今后两者分叉时互相影响。 */
  roomRenovationRecord: {
    '1000': { type: 'warning', label: '待审核' },
    '2000': { type: 'danger', label: '审核不通过' },
    '3000': { type: 'primary', label: '装修中' },
    '4000': { type: 'warning', label: '待验收' },
    '5000': { type: 'success', label: '验收成功' },
    '6000': { type: 'danger', label: '验收失败' }
  },
  /* 设备 / IoT */
  device: {
    '0': { type: 'danger', label: '离线' },
    '1': { type: 'success', label: '在线' },
    '2': { type: 'warning', label: '告警' }
  },
  /* 房屋 / 商铺（building_room.state）：
   * 2001 已入住 / 2002 未销售 / 2003 已交房 / 2004 未入住 / 2005 已装修 /
   * 2006 已出租 / 2007 已出售 / 2008 空闲 / 2009 装修中
   * 注意：roomList 中「交房」按钮以 state == '2002'（未销售）为判定条件，
   * 改动本映射时不可改变状态码本身。 */
  room: {
    '2001': { type: 'success', label: '已入住' },
    '2002': { type: 'warning', label: '未销售' },
    '2003': { type: 'success', label: '已交房' },
    '2004': { type: 'warning', label: '未入住' },
    '2005': { type: 'success', label: '已装修' },
    '2006': { type: 'success', label: '已出租' },
    '2007': { type: 'success', label: '已出售' },
    '2008': { type: 'warning', label: '空闲' },
    '2009': { type: 'primary', label: '装修中' }
  },
  /* 通用启用/停用 */
  common: {
    '0': { type: 'success', label: '启用' },
    '1': { type: 'muted', label: '停用' },
    Y: { type: 'success', label: '是' },
    N: { type: 'muted', label: '否' }
  }
}

/**
 * 从语义词表推断类型
 * @param {string} text
 * @returns {string|null}
 */
function inferTypeByText (text) {
  if (!text) return null
  const lower = String(text).toLowerCase()
  // 优先级：danger > warning > success > primary > info
  const order = ['danger', 'warning', 'success', 'primary', 'info']
  for (const type of order) {
    const keywords = SEMANTIC_KEYWORDS[type]
    for (const kw of keywords) {
      if (lower.indexOf(kw.toLowerCase()) > -1) return type
    }
  }
  return null
}

/**
 * 解析状态，返回统一结构
 *
 * @param {string|number} value 后端状态值
 * @param {object} [options]
 * @param {string} [options.domain] 业务域，见 DOMAIN_MAPS 的 key
 * @param {string} [options.text] 状态文案（后端 stateName 等），用于语义推断
 * @param {string} [options.fallbackLabel] 未命中时的展示文案，默认为 value 原文
 * @returns {{ type: string, label: string }}
 */
export function resolveStatus (value, options = {}) {
  const { domain = '', text = '', fallbackLabel = '' } = options

  // 1. 命中业务域字典
  if (domain && DOMAIN_MAPS[domain]) {
    const map = DOMAIN_MAPS[domain]
    const key = value === null || value === undefined ? '' : String(value)
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      const hit = map[key]
      return { type: hit.type, label: hit.label || text || fallbackLabel || key }
    }
  }

  // 2. 用状态文案做语义推断
  const label = text || fallbackLabel || (value === null || value === undefined ? '' : String(value))
  const inferred = inferTypeByText(label)
  if (inferred) {
    return { type: inferred, label: label || EMPTY_LABEL }
  }

  // 3. 安全降级：保留原始信息
  return { type: 'muted', label: label || EMPTY_LABEL }
}

const EMPTY_LABEL = '—'

/**
 * 判断状态是否为「积极结果」（用于表格行高亮等）
 * @param {string} type
 * @returns {boolean}
 */
export function isPositiveStatus (type) {
  return type === 'success'
}

/**
 * 判断状态是否为「需要关注」
 * @param {string} type
 * @returns {boolean}
 */
export function isAlertStatus (type) {
  return type === 'warning' || type === 'danger'
}

/**
 * 注册业务域状态字典（供后续模块扩展，不影响已有映射）
 * @param {string} domain
 * @param {object} map
 */
export function registerStatusDomain (domain, map) {
  if (!domain || !map) return
  DOMAIN_MAPS[domain] = Object.assign({}, DOMAIN_MAPS[domain] || {}, map)
}

export default {
  DS_STATUS_TYPES,
  resolveStatus,
  isPositiveStatus,
  isAlertStatus,
  registerStatusDomain
}
